import { cn } from '@/lib/utils'

// 小八系列素材类型
type HachiwareImageType = 
  | 'hachiwareCute1' | 'hachiwareCute2' | 'hachiwareCute3'
  | 'hachiwareDaily1' | 'hachiwareDaily2' | 'hachiwareDaily3' | 'hachiwareDaily4' | 'hachiwareDaily5' | 'hachiwareDaily6' | 'hachiwareDaily7'
  | 'hachiwareAvatar1' | 'hachiwareAvatar2'
  | 'hachiwareFace6' | 'hachiwareFace7' | 'hachiwareFace8' | 'hachiwareFace9' | 'hachiwareFace10'
  | 'hachiwareCamera1' | 'hachiwareCamera2'
  | 'hachiwareLineStamp'

interface EmptyStateProps {
  title?: string
  description?: string
  className?: string
  size?: 'sm' | 'md' | 'lg'
  character?: 'chiikawa' | 'hachiware' | 'usagi' | 'kuri' | 'rakko' | HachiwareImageType
}

const characterImages: Record<string, string> = {
  chiikawa: '/images/chiikawa.png',
  hachiware: '/images/hachiware.png',
  usagi: '/images/usagi.png',
  kuri: '/images/kuri.png',
  rakko: '/images/rakko.png',
  // 小八系列 JPG 素材
  hachiwareCute1: '/images/hachiware_cute_1.jpg',
  hachiwareCute2: '/images/hachiware_cute_2.jpg',
  hachiwareCute3: '/images/hachiware_cute_3.jpg',
  hachiwareDaily1: '/images/hachiware_daily_1.jpg',
  hachiwareDaily2: '/images/hachiware_daily_2.jpg',
  hachiwareDaily3: '/images/hachiware_daily_3.jpg',
  hachiwareDaily4: '/images/hachiware_daily_4.jpg',
  hachiwareDaily5: '/images/hachiware_daily_5.jpg',
  hachiwareDaily6: '/images/hachiware_daily_6.jpg',
  hachiwareDaily7: '/images/hachiware_daily_7.jpg',
  hachiwareAvatar1: '/images/hachiware_avatar_1.jpg',
  hachiwareAvatar2: '/images/hachiware_avatar_2.jpg',
  // 小八其他系列素材
  hachiwareFace6: '/images/hachiware_face_6.jpg',
  hachiwareFace7: '/images/hachiware_face_7.jpg',
  hachiwareFace8: '/images/hachiware_face_8.jpg',
  hachiwareFace9: '/images/hachiware_face_9.jpg',
  hachiwareFace10: '/images/hachiware_face_10.jpg',
  hachiwareCamera1: '/images/hachiware_camera_1.jpg',
  hachiwareCamera2: '/images/hachiware_camera_2.jpg',
  hachiwareLineStamp: '/images/line_stamp_poinko.jpg',
}

const characterColors: Record<string, string> = {
  chiikawa: 'bg-chiikawa-pink-light border-chiikawa-pink/30',
  hachiware: 'bg-chiikawa-blue-light border-chiikawa-blue/30',
  usagi: 'bg-chiikawa-yellow-light border-chiikawa-yellow/30',
  kuri: 'bg-chiikawa-cream border-chiikawa-peach/30',
  rakko: 'bg-chiikawa-lavender border-chiikawa-lavender',
}

/**
 * 吉伊卡哇风格空状态组件
 * 支持多个角色：吉伊、小八、乌萨奇、栗子、海獭
 */
export function EmptyState({
  title = '暂无数据',
  description,
  className,
  size = 'md',
  character = 'hachiware',
}: EmptyStateProps) {
  const sizeClasses = {
    sm: 'w-20 h-20',
    md: 'w-28 h-28',
    lg: 'w-36 h-36',
  }

  const textSizes = {
    sm: { title: 'text-sm', desc: 'text-xs' },
    md: { title: 'text-base', desc: 'text-sm' },
    lg: { title: 'text-lg', desc: 'text-base' },
  }

  const fallbackSvg = getFallbackSvg(character)

  return (
    <div className={cn('flex flex-col items-center justify-center py-8', className)}>
      {/* 角色图片插图 */}
      <div className={cn(
        sizeClasses[size],
        'mb-4 rounded-full overflow-hidden flex items-center justify-center',
        'border-3 shadow-md',
        characterColors[character]
      )}>
        <img
          src={characterImages[character]}
          alt={character}
          className="w-full h-full object-cover"
          onError={(e) => {
            const target = e.target as HTMLImageElement
            target.style.display = 'none'
            const parent = target.parentElement
            if (parent) {
              parent.innerHTML = fallbackSvg
            }
          }}
        />
      </div>

      {/* 文字内容 */}
      <p className={cn('font-medium text-chiikawa-brown', textSizes[size].title)}>
        {title}
      </p>
      {description && (
        <p className={cn('text-chiikawa-brown/60 mt-1', textSizes[size].desc)}>
          {description}
        </p>
      )}
    </div>
  )
}

// 备用 SVG
function getFallbackSvg(character: string): string {
  const svgs: Record<string, string> = {
    chiikawa: `<svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="50" cy="50" r="45" fill="#FFE4E9"/><ellipse cx="50" cy="58" rx="32" ry="28" fill="white" stroke="#FFB6C1" stroke-width="2"/><ellipse cx="35" cy="38" rx="8" ry="10" fill="white" stroke="#FFB6C1" stroke-width="2"/><ellipse cx="65" cy="38" rx="8" ry="10" fill="white" stroke="#FFB6C1" stroke-width="2"/><circle cx="40" cy="52" r="4" fill="#333"/><circle cx="60" cy="52" r="4" fill="#333"/><ellipse cx="30" cy="58" rx="5" ry="3" fill="#FFB6C1"/><ellipse cx="70" cy="58" rx="5" ry="3" fill="#FFB6C1"/><ellipse cx="50" cy="58" rx="3" ry="2" fill="#333"/><path d="M47 63 Q50 66 53 63" stroke="#333" stroke-width="1.5" fill="none"/></svg>`,
    hachiware: `<svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="50" cy="50" r="45" fill="#E0F4FF"/><path d="M20 35 Q50 20 80 35 Q80 25 70 20 Q60 25 50 25 Q40 25 30 20 Q20 25 20 35" fill="#87CEEB" stroke="#5DADE2" stroke-width="2"/><ellipse cx="50" cy="60" rx="30" ry="28" fill="white" stroke="#87CEEB" stroke-width="2"/><path d="M25 32 L20 15 Q23 10 30 15 L35 30" fill="#87CEEB" stroke="#5DADE2" stroke-width="2"/><path d="M75 32 L80 15 Q77 10 70 15 L65 30" fill="#87CEEB" stroke="#5DADE2" stroke-width="2"/><circle cx="38" cy="50" r="5" fill="#333"/><circle cx="62" cy="50" r="5" fill="#333"/><ellipse cx="28" cy="58" rx="6" ry="4" fill="#FFB6C1"/><ellipse cx="72" cy="58" rx="6" ry="4" fill="#FFB6C1"/><ellipse cx="50" cy="56" rx="3" ry="2" fill="#333"/><path d="M47 62 Q50 66 53 62" stroke="#333" stroke-width="1.5" fill="none"/></svg>`,
    usagi: `<svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="50" cy="50" r="45" fill="#FFF8E7"/><ellipse cx="50" cy="60" rx="30" ry="28" fill="#FFE4A1" stroke="#FFD700" stroke-width="2"/><ellipse cx="35" cy="25" rx="6" ry="15" fill="#FFE4A1" stroke="#FFD700" stroke-width="2"/><ellipse cx="65" cy="25" rx="6" ry="15" fill="#FFE4A1" stroke="#FFD700" stroke-width="2"/><circle cx="40" cy="50" r="4" fill="#333"/><circle cx="60" cy="50" r="4" fill="#333"/><ellipse cx="50" cy="58" rx="4" ry="3" fill="#FF6B6B"/><path d="M46 64 Q50 68 54 64" stroke="#333" stroke-width="1.5" fill="none"/></svg>`,
    kuri: `<svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="50" cy="50" r="45" fill="#FFF9F0"/><ellipse cx="50" cy="60" rx="32" ry="28" fill="#8B7355" stroke="#6B5344" stroke-width="2"/><ellipse cx="50" cy="38" rx="35" ry="18" fill="#FFF9F0" stroke="#8B7355" stroke-width="2"/><circle cx="38" cy="52" r="4" fill="#333"/><circle cx="62" cy="52" r="4" fill="#333"/><ellipse cx="50" cy="58" rx="3" ry="2" fill="#333"/></svg>`,
    rakko: `<svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="50" cy="50" r="45" fill="#E6E6FA"/><ellipse cx="50" cy="60" rx="30" ry="28" fill="#DDA0DD" stroke="#9370DB" stroke-width="2"/><circle cx="40" cy="50" r="4" fill="#333"/><circle cx="60" cy="50" r="4" fill="#333"/><ellipse cx="50" cy="58" rx="4" ry="3" fill="#333"/><path d="M35 70 Q50 75 65 70" stroke="#9370DB" stroke-width="2" fill="none"/></svg>`,
  }
  return svgs[character] || svgs.hachiware
}

/**
 * 订单空状态 - 小八 + 订单相关文案
 */
export function EmptyOrdersState({ className }: { className?: string }) {
  return (
    <EmptyState
      title="今天还没有预约哦"
      description="点击右上角按钮创建新订单"
      size="md"
      character="hachiwareDaily2"
      className={className}
    />
  )
}

/**
 * 顾客空状态 - 吉伊
 */
export function EmptyCustomersState({ className }: { className?: string }) {
  return (
    <EmptyState
      title="暂无顾客"
      description="添加您的第一位顾客"
      size="md"
      character="hachiwareDaily3"
      className={className}
    />
  )
}

/**
 * 妹妹空状态 - 海獭师傅
 */
export function EmptyGirlsState({ className }: { className?: string }) {
  return (
    <EmptyState
      title="暂无妹妹信息"
      description="添加第一位妹妹"
      size="md"
      character="hachiwareDaily4"
      className={className}
    />
  )
}

/**
 * 数据空状态 - 乌萨奇
 */
export function EmptyDataState({ className }: { className?: string }) {
  return (
    <EmptyState
      title="暂无数据"
      size="sm"
      character="hachiwareDaily5"
      className={className}
    />
  )
}

/**
 * 标签空状态 - 栗子
 */
export function EmptyTagsState({ className }: { className?: string }) {
  return (
    <EmptyState
      title="暂无标签"
      description="创建您的第一个标签"
      size="sm"
      character="hachiwareCute3"
      className={className}
    />
  )
}
